<template>
  <fts-dropdown-select
    append-to-body
    placement="bottom-end"
    prevent-overflow-alt-axis
    prevent-overflow-tether-off
    append-toggle-class="assign-toggle"
    content-class="dropdown-menu dropdown-menu-w-md assign-content assign-dropdown-menu"
    :offset="[0, 6]"
    :disabled="disabled"
    :reference-id="`fts-popper tippy-popper vs__dropdown-menu ${referenceId}`"
    :style="{'--toggle-height': avatarSize + 'px'}"
    @show="show"
    @hide="hide"
    v-if="isAssigneeToggleVisible || isDueToggleVisible"
  >
    <template slot="toggle">
      <fts-tippy
        trigger-class="assigned-assignee-wrapper"
        content="Assignees"
        v-if="isAssigneeToggleVisible"
      >
        <template>
          <div
            class="assigned-assignee"
            :class="{'assignees': assignees.length > 1}"
            v-if="assignees.length"
          >
            <template v-for="x in assignees">
              <fts-profile 
                :user-name="x.userName" 
                :profile-photo-size="avatarSize"
                :profile-name="assignees.length === 1 && !avatarMode" 
                :key="x.username" 
                class="assignee-profile"
                v-if="x.type === 'profile'"
              />
              <div
                class="assignee-profile-count"
                :style="{ height: `${avatarSize}px`, width: `${avatarSize}px`, minWidth: `${avatarSize}px`}"
                :key="x.type"
                v-else
              >
                {{x.count}}
              </div>
            </template>
          </div>

          <div
            class="assign-toggle-action"
            :style="{ 'height': `${avatarSize}px`, 'width': `${avatarSize}px` }"
            v-if="toggleCreate && !disabled && !viewMode"
          > 
            <i class="fts-icon-user-plus"></i>
          </div>
        </template>
      </fts-tippy>

      <fts-tippy
        :content="`due on ${moment(formData.due).format('ll')}`"
        class="assigned-due-wrapper"
        :key="formData.id + formData.due"
        v-if="isDueToggleVisible"
      >
        <div class="assigned-due" :class="{'assigned-due--overdue': overdue}">
          {{calendarRelativeTime}}
        </div>
      </fts-tippy>
    </template>
    <!-- menu -->
    <template #menu="{select}">
      <fts-assign
        :assign-data="assignData"
        :view-mode="viewMode"
        :reference-for="referenceId"
      />
      <fts-comment
        :vector-id="module.id"
        :comment="assignData.comment"
        class="assign-comment"
        v-if="commentSection"
      />
      <div class="fts-text-right" v-if="(!viewMode || commentSection ) && !updateMode">
        <fts-btn text @click="select()">
          <font-awesome-icon :icon="['fas', 'times']" /> Cancel
        </fts-btn>
        <fts-btn color="primary" box-shadow-none @click="submit();select()">
          <font-awesome-icon :icon="['fas', 'check']" /> Ok
        </fts-btn>
      </div>
    </template>
  </fts-dropdown-select>
</template>

<script>
import moment from "moment";
import commons from '../commons/commons';
import { classificationStore } from "../store/classificationStore";

import FTSDropdownSelect from "./FTSDropdownSelect";
import FTSTippy from "./FTSTippy";
import FTSProfile from "./FTSProfile";
import FTSBtn from "./FTSBtn";
import FTSComment from "./FTSComment";
import FTSAssign from "./FTSAssign";

export default {
  props: {
    moduleId: [String, Number],
    formData: {
      type: Object,
      default: () => ({})
    },
    maxLength: {
      type: Number,
      default: 5
    },
    viewMode: Boolean,
    updateMode: Boolean,
    submitOnClose: Boolean,
    avatarMode: Boolean,
    avatarSize: {
      type: Number,
      default: 20,
    },
    commentSection: {
      type: Boolean,
      default: true,
    },
    toggleDue: {
      type: Boolean,
      default: true
    },
    toggleCreate: { 
      type: Boolean,
      default: true
    },
    disabledDate: Boolean,
    disabled: Boolean
  },
  components: {
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-tippy": FTSTippy,
    "fts-profile": FTSProfile,
    "fts-btn": FTSBtn,
    "fts-comment": FTSComment,
    "fts-assign": FTSAssign,
  },
  data: () => ({
    assignData: {
      assignees: [],
      due: null,
      comment: {}
    },
  }),
  methods: {
    moment,
    show() {
      try {
        if (Array.isArray(this.formData.assignees))
          this.assignData.assignees = this.formData.assignees;
        else 
          throw 'error';
      } catch(err) {
        this.assignData.assignees = []
      }
      this.assignData.due = this.formData.due;
    },
    hide() {
      if (this.submitOnClose) {
        this.submit();
      }

      this.assignData = {
        assignees: [],
        due: null,
        comment: { }
      }
      this.$emit('close');
    },
    async submit() {
      const submitData = {
        moduleId: this.moduleId,
        activityId: 'assign',
        entryIds: [this.formData.id],
        post: this.assignData.comment,
        assignees: this.assignData.assignees,
        due: this.assignData.due ? moment(this.assignData.due).endOf('day') : null
      }
      this.$emit("update:formData", {
        ...this.formData,
        assignees: submitData.assignees,
        due: submitData.due
      });

      if (!this.updateMode) {
        await classificationStore.dispatch('submitActivity', submitData);
        this.$emit('refresh');
      }
    },
  },
  computed: {
    module() {
      return classificationStore.state.vector || {};
    },
    assignees() {
      const assignees = this.formData.assignees || [];
      if (this.maxLength && (assignees.length > this.maxLength)) {
        const count = assignees.slice(this.maxLength - 1, assignees.length).length;
        return assignees
          .slice(0, this.maxLength - 1)
          .map((x) => ({ type: 'profile', userName: x }))
          .concat({ type: 'count', count: count > 99 ? 99 : count });
      } else {
        return assignees.map((x) => ({ type: 'profile', userName: x }));
      }
    },
    overdue() {
      if (this.formData && this.formData.due)
        return moment(this.formData.due).isBefore(moment().startOf('day'));
    },
    calendarRelativeTime() {
      if (this.formData && this.formData.due)
        return commons.calendarRelativeTime(this.formData.due);
    },
    isAssigneeToggleVisible() {
      return Boolean(this.assignees && this.assignees.length) || Boolean(this.toggleCreate && !this.disabled && !this.viewMode);
    },
    isDueToggleVisible() {
      return Boolean(this.toggleDue && !this.disabledDate && this.formData.due && moment(this.formData.due).isValid());
    }
  },
  created() {
    this.referenceId = `inistate-ref-${this._uid}`;
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.assign-toggle {
  min-height: var(--toggle-height);
  display: flex;
  align-items: center;
  .assigned-assignee-wrapper {
    display: flex;
    align-items: center;
  }
  .assigned-assignee {
    &:not(.assignees) {
      min-width: 0;
    }  
    display: flex;
    align-items: center;
    z-index: 1;
    .assignee-profile {
      .profile-photo {
        box-shadow: 0 0 0 1px var(--fts-gray-100);
      }
      &:not(:first-child) {
        margin-left: calc(var(--toggle-height) / 5 * -1);
      }
    }
    .assignee-profile-count {
      @include flex-center-middle;
      @include theme {
        background-color: lighten(theme-get('fts-primary'), 40%);
      }
      margin-left: calc(var(--toggle-height) / 5 * -1);
      border-radius: 50%;
      color: var(--fts-primary);
      font-size: 75%;
    }
  }
  .assigned-due-wrapper {
    white-space: nowrap;
    .assigned-due { 
      color: var(--fts-default);
      font-size: 0.875rem;
      &.assigned-due--overdue {
        color: var(--fts-danger);
      }
    }
    &:not(:first-child) {
      margin-left: 0.375rem;
    }
  }
  .assign-toggle-action {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--fts-default);
    cursor: pointer;
    &:not(:first-child) {
      margin-left: 0.125rem;
    }
  }
}
.assign-content.assign-dropdown-menu {
  padding: 1rem;
  cursor: initial;
  .assign-comment {
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
}
</style>